import React, { useContext, useReducer, useState } from "react"
import { CarrotErrorCodes, getErrorMessageFromCode } from "utils/CarrotErrors"
import CarrotClient from "lib/carrot-api/index"
import { reportError } from "utils/ErrorReporting"
import { isHttpError } from "utils/HttpErrors"
import { redirectToSamlExternalLogin } from "utils/RedirectToSamlExternalLogin"
import Settings from "utils/CarrotConfig"
import { useSelector } from "react-redux"
import { loginEmail } from "reducers/loginInfo"
import { useSignUpFlow } from "../../context/signup/SignUpFlowContext"
import { getHeap } from "../../../utils/heap"

interface SignUpReducerState {
  username: string
  errorMessage: string
  inputsEnabled: boolean
  showSubmittingLabel: boolean
  success: boolean
  shake: boolean
  showTryAgain: boolean
}

interface SignUpProviderProps extends SignUpReducerState {
  onSubmit: () => void
}

const client = new CarrotClient(Settings.CARROT_BACKEND_URL)

const SignUpContext = React.createContext<SignUpProviderProps>(null)

export const useSignUp = () => useContext(SignUpContext)

const actionTypes = {
  error: "ERROR",
  start: "REQUEST_START",
  success: "REQUEST_SUCCESS",
  tryAgainMessage: "TRY_AGAIN_MESSAGE"
}

function signUpReducer(state, action): SignUpReducerState {
  switch (action.type) {
    case actionTypes.error: {
      return {
        ...state,
        showTryAgain: false,
        inputsEnabled: true,
        showSubmittingLabel: false,
        shake: true,
        errorMessage: getErrorMessageFromCode(action.payload.errorCode)
      }
    }
    case actionTypes.start: {
      return {
        ...state,
        inputsEnabled: true,
        showSubmittingLabel: true,
        shake: false
      }
    }
    case actionTypes.success: {
      return {
        ...state,
        showTryAgain: false,
        inputsEnabled: false,
        showSubmittingLabel: false,
        success: true
      }
    }
    case actionTypes.tryAgainMessage: {
      return {
        ...state,
        showTryAgain: true,
        inputsEnabled: true,
        showSubmittingLabel: false,
        shake: true,
        errorMessage: null
      }
    }
    default: {
      throw new Error(`Unhandled type: ${action.type}`)
    }
  }
}

export function SignUpProvider({ handleSubmit, setError, children, onSuccessfulSignUp }) {
  const username = useSelector(loginEmail)
  const [state, dispatch] = useReducer(signUpReducer, {
    username,
    errorMessage: null,
    inputsEnabled: true,
    showSubmittingLabel: false,
    success: false,
    shake: false,
    showTryAgain: false
  })
  const { handleSaveMemberInfoInMemory } = useSignUpFlow()

  const setFormError = (errorCode) => dispatch({ type: actionTypes.error, payload: { errorCode } })
  const startRequest = () => dispatch({ type: actionTypes.start })
  const endSuccessfulRequest = () => dispatch({ type: actionTypes.success })
  const showTryAgain = () => dispatch({ type: actionTypes.tryAgainMessage })
  const [tryAgainSubmission, setTryAgainSubmission] = useState(false)

  const setErrorState = (errorCode): void => {
    setFormError(errorCode)
    setError("email")
  }

  const lookupAccount = async (email) => {
    try {
      const response = await client.lookup(email)

      if (response?.employee?.samlIdpEntityId) {
        const { samlIdpEntityId, isAdmin } = response.employee
        redirectToSamlExternalLogin(samlIdpEntityId, isAdmin)
        return false
      }
      if (response?.status === CarrotErrorCodes.EMPLOYEE_INACTIVE) {
        setFormError(response.status)
        return false
      }
      if (response?.employee?.isRegistered) {
        setFormError(CarrotErrorCodes.EMPLOYEE_ALREADY_REGISTERED)
        return false
      } else {
        return true
      }
    } catch (error) {
      setFormError(CarrotErrorCodes.INTERNAL_SERVER_ERROR)
      return false
    }
  }

  const onSubmit = handleSubmit(async ({ email }) => {
    startRequest()

    if (tryAgainSubmission) {
      getHeap().track("NoEmailOnboarding", { EventName: "sign up try again submission" })
    }
    setTryAgainSubmission(true)
    let shouldSignUp
    try {
      shouldSignUp = await lookupAccount(email)
      shouldSignUp && (await client.signUp(email))
    } catch (error) {
      if (isHttpError(error)) {
        const { code } = await error.response.json()
        if (code === CarrotErrorCodes.EMPLOYEE_NOT_FOUND) {
          handleSaveMemberInfoInMemory({ email })
          showTryAgain()
          return
        }

        setErrorState(code)
        return
      }

      reportError(error)
      setErrorState(CarrotErrorCodes.UNEXPECTED_ERROR)
      return
    }
    if (shouldSignUp) {
      endSuccessfulRequest()
      onSuccessfulSignUp()
    }
  })

  return <SignUpContext.Provider value={{ ...state, onSubmit }}>{children}</SignUpContext.Provider>
}
